import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppContact, AppIndex, AppRow } from "./AppIndex";
import { AppTaskChip, AppTaskGroup, AppTaskRules, AppTaskStrip } from "./AppTaskRules";
import { AppTaskApi } from "./AppTaskApi";
import { AppTaskTips } from "./AppTaskTips";
import { AppIcons } from "./AppIcons";
import { AppAvatarRules } from "./AppAvatars";

// AppTaskView - the markup of the new Tasks tab and the Today strip. `AppTaskRules` decides what a
// row says; `AppTaskBoard` wires it and changes it.
//
// ⛔ EVERY WORD FROM A CONVERSATION IS ESCAPED (`kHTMLCore.EscapeHtml`) - labels and values are the
// harvest's (the model's) and contact names are whatever WhatsApp said, so `<img onerror>` in a task
// is shown as those characters. Nothing from a row is ever put into an attribute unescaped, and an
// id never enters a selector (`AppTaskBoard` compares them in a loop).
// ⚠ BROWSED, NOT READ (the 2026-09-12 ruling): label bold, value beside it, then one quiet meta line.

export class AppTaskView {
    public static readonly TabId: string = "wac-tasks";
    public static readonly ListId: string = "wac-tlist";
    public static readonly SearchId: string = "wac-tfind";
    public static readonly SayId: string = "wac-tsay";
    public static readonly AddFormId: string = "wac-tadd";
    public static readonly StripId: string = "wac-today";
    public static readonly StripListId: string = "wac-tstrip";
    public static readonly StripSayId: string = "wac-tstripsay";
    /** A button's action: done | reopen | snooze | dismiss | reply | pickdate. */
    public static readonly ActAttr: string = "data-wac-tact";
    public static readonly IdAttr: string = "data-wac-tid";
    public static readonly UntilAttr: string = "data-wac-until";
    public static readonly FilterAttr: string = "data-wac-tfilter";
    /** On the `<summary>` of a folded section (`AppTaskRules.Folded`): which one, so the board remembers it open. */
    public static readonly FoldAttr: string = "data-wac-tfold";
    /** On a row's own link (`OpenLink`): the chat's href. */
    public static readonly OpenAttr: string = "data-wac-topen";
    /** On a reply-owed row's link: open the chat with the message box focused. */
    public static readonly OpenReplyAttr: string = "data-wac-treply";
    public static readonly FilterWords: Record<string, string> = { mine: "Mine", theirs: "Theirs", all: "All" };

    // ── The Tasks tab ────────────────────────────────────────────────────────────────────────

    /**
     * The whole tab: search + add above, the filters and sections in `ListId` (re-rendered alone).
     * `OffsetMin` is the browser's UTC offset (`AppTaskRules.OffsetOf`); `Open` the folded sections
     * the owner has opened (so a re-render after a tick does not fold them again).
     */
    public static Tab = (Index: AppIndex, Today: string, OffsetMin: number, Who: string, Search: string,
                         Pending: Record<string, boolean>, Open: Record<string, boolean>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<section class=\"tasksview\" id=\"" + AppTaskView.TabId + "\">"
                + "<div class=\"tbar\"><div class=\"find tfind\" role=\"search\" aria-label=\"Search tasks\"><label class=\"sr\" for=\""
                + AppTaskView.SearchId + "\">Search tasks</label><input id=\"" + AppTaskView.SearchId
                + "\" type=\"search\" autocomplete=\"off\" spellcheck=\"false\" title=\"Search tasks by task, detail or name\""
                + " value=\"" + kHTMLCore.EscapeAttr(Search) + "\"></div></div>" + AppTaskView.AddForm(Index)
                + "<p class=\"tsay\" id=\"" + AppTaskView.SayId + "\" role=\"status\" aria-live=\"polite\"></p>"
                + "<div id=\"" + AppTaskView.ListId + "\">" + AppTaskView.List(Index, Today, OffsetMin, Who, Search,
                    Pending, Open) + "</div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Tab");
            return "";
        }
    };

    /** The filter chips and the sections - what a search or a change re-renders. */
    public static List = (Index: AppIndex, Today: string, OffsetMin: number, Who: string, Search: string,
                          Pending: Record<string, boolean>, Open: Record<string, boolean>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [AppTaskView.FilterBar(Index, Today, Who)];
            if (Index.Tasks.length === 0) { Out.push(AppTaskView.Empty(Index)); return Out.join(""); }
            const Rows: AppRow[] = AppTaskRules.Filter(Index.Tasks, Who, Search);
            const Groups: AppTaskGroup[] = AppTaskRules.Groups(Rows, Today, OffsetMin);
            if (Groups.length === 0) { Out.push(AppTaskView.NoneMatch(Who, Search)); return Out.join(""); }
            // ⚠ A SEARCH OPENS THE FOLDED SECTIONS - a match hidden under "Older (3)" reads as no match.
            const Searching: boolean = nz(Search).trim().length > 0;
            for (const Group of Groups) {
                Out.push(AppTaskView.Section(Group, Today, OffsetMin, Pending, Searching || Open[Group.Key] === true));
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.List");
            return "";
        }
    };

    /** Mine / Theirs / All, each with its count of OPEN tasks. */
    private static FilterBar = (Index: AppIndex, Today: string, Who: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<div class=\"tfilter\" role=\"group\" aria-label=\"Whose tasks\">"];
            for (const Key of AppTaskRules.Filters) {
                let Open: number = 0;
                for (const One of AppTaskRules.Filter(Index.Tasks, Key, "")) {
                    if (AppTaskRules.IsOpen(One, Today) === true) { Open = Open + 1; }
                }
                Out.push("<button type=\"button\" class=\"tf\" " + AppTaskView.FilterAttr + "=\"" + Key
                    + "\" aria-pressed=\"" + String(Key === Who) + "\">" + AppTaskView.FilterWords[Key]
                    + "<i>" + Open.toLocaleString() + "</i></button>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.FilterBar");
            return "";
        }
    };

    /**
     * One section. ⭐ A FOLDED ONE (`AppTaskRules.Folded`: "Older") is a `<details>` whose summary reads
     * "Older (N)" - closed until clicked, by keyboard too, without script; `IsOpen` keeps it open.
     */
    private static Section = (Group: AppTaskGroup, Today: string, OffsetMin: number, Pending: Record<string, boolean>,
                              IsOpen: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Count: string = Group.Rows.length.toLocaleString();
            const Folded: boolean = AppTaskRules.Folded.indexOf(Group.Key) >= 0;
            const Out: string[] = [];
            if (Folded === true) {
                let Opened: string = "";
                if (IsOpen === true) { Opened = " open"; }
                Out.push("<details class=\"tgroup tfold g-" + Group.Key + "\"" + Opened + "><summary "
                    + AppTaskView.FoldAttr + "=\"" + Group.Key + "\">" + AppIcons.Svg(AppIcons.ChevronDown, 16) + "<h2>" + kHTMLCore.EscapeHtml(Group.Title)
                    + " (" + Count + ")</h2></summary><ul class=\"tasks\">");
            } else {
                // ⚠ `<h2>`: the groups sit right under the view's `<h1>` (`AppViewHead`); they were `<h3>` under nothing.
                Out.push("<section class=\"tgroup g-" + Group.Key + "\"><h2>" + kHTMLCore.EscapeHtml(Group.Title)
                    + "<i>" + Count + "</i></h2><ul class=\"tasks\">");
            }
            for (let At: number = 0; At < Group.Rows.length; At = At + 1) {
                Out.push(AppTaskView.Row(Group.Rows[At], At, Today, OffsetMin, Pending, false));
            }
            if (Folded === true) { Out.push("</ul></details>"); } else { Out.push("</ul></section>"); }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Section");
            return "";
        }
    };

    /**
     * One task: the round tick (or ↩ for a reply owed, which opens the chat), the title in bold
     * with its value, then contact · due chip · owner · age · "↗ message", and the "…" menu.
     * ⚠ A ROW WITH NO SERVER ID GETS NO TICK AND NO MENU - it cannot be changed (`CanAct`).
     */
    public static Row = (Row: AppRow, At: number, Today: string, OffsetMin: number, Pending: Record<string, boolean>,
                         Compact: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Key: string = AppTaskRules.KeyOf(Row, At);
            const Bucket: string = AppTaskRules.Bucket(Row, Today, OffsetMin);
            let Cls: string = "task b-" + Bucket;
            if (AppTaskRules.IsReply(Row) === true) { Cls = Cls + " reply"; }
            if (Pending[Row.Id] === true && Row.Id.length > 0) { Cls = Cls + " tpending"; }
            let Menu: string = "";
            if (Compact === false && AppTaskRules.CanAct(Row) === true) { Menu = AppTaskView.ReopenButton(Row, Today) + AppTaskView.Menu(Row, Today); }
            let Title: string = "<b>" + kHTMLCore.EscapeHtml(AppTaskRules.TitleOf(Row)) + "</b>";
            const Link: string = AppTaskRules.LinkOf(Row);
            if (Link.length > 0) {
                Cls = Cls + " topen";
                Title = AppTaskView.OpenLink(Row, Link, Today, Title);
            }
            return "<li class=\"" + Cls + "\" data-wac-tkey=\"" + kHTMLCore.EscapeAttr(Key) + "\">"
                + AppTaskView.Lead(Row, Today, Pending) + "<div class=\"tbody\"><p class=\"tl\">"
                + Title + AppTaskView.Value(Row, Compact)
                + "</p><p class=\"tm\">" + AppTaskView.Meta(Row, Today, Compact) + "</p></div>" + Menu + "</li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Row");
            return "";
        }
    };

    /**
     * ⭐ THE WHOLE ROW IS ONE LINK (owner, 2026-09-27: "This whole panel should be clickable"): the
     * title is a real `<a>` (Tab focus, Enter, middle-click, a URL on hover) whose `::after` is
     * stretched over the whole `li` by `app.css` (`.task.topen a.trow::after`). The tick, the "…"
     * menu and "↗ message" are lifted ABOVE that layer, so they stay their own targets and a tick
     * never navigates. Same destination as "↗ message"; a reply owed also marks `OpenReplyAttr`, and
     * `AppTaskBoard.OnClick` asks the host to focus the message box once the chat opens.
     * ⛔ It only navigates - nothing is sent or queued (decision 9).
     */
    private static OpenLink = (Row: AppRow, Link: string, Today: string, Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return Title; }
        try {
            let Reply: string = "";
            let Tip: string = "Open the chat at this message";
            if (AppTaskRules.IsReply(Row) === true && AppTaskRules.IsOpen(Row, Today) === true) {
                Reply = " " + AppTaskView.OpenReplyAttr + "=\"1\"";
                Tip = "Open the chat to reply";
            }
            return "<a class=\"trow\" href=\"" + kHTMLCore.EscapeAttr(Link) + "\" " + AppTaskView.OpenAttr + "=\""
                + kHTMLCore.EscapeAttr(Row.Href) + "\"" + Reply + " title=\"" + Tip + "\">" + Title + "</a>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.OpenLink");
            return Title;
        }
    };

    /** The value beside the title - a reply owed has none worth repeating. */
    private static Value = (Row: AppRow, Compact: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsReply(Row) === true || Row.Value.length === 0 || Row.Label.length === 0) { return ""; }
            if (Compact === true) { return ""; }
            return " <span class=\"tv\">" + kHTMLCore.EscapeHtml(Row.Value) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Value");
            return "";
        }
    };

    /** The round tick - or, for a reply owed, a ↩ that opens the chat (its primary action). */
    private static Lead = (Row: AppRow, Today: string, Pending: Record<string, boolean>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Title: string = kHTMLCore.EscapeAttr(AppTaskRules.TitleOf(Row));
            if (AppTaskRules.IsReply(Row) === true && AppTaskRules.IsOpen(Row, Today) === true) {
                return "<a class=\"tick treply\" href=\"" + kHTMLCore.EscapeAttr(AppTaskRules.LinkOf(Row))
                    + "\" " + AppTaskView.OpenAttr + "=\"" + kHTMLCore.EscapeAttr(Row.Href) + "\" "
                    + AppTaskView.OpenReplyAttr + "=\"1\" title=\"Open the chat\" aria-label=\"Open the chat: " + Title + "\">"
                    + AppIcons.Svg(AppIcons.Reply, 14) + "</a>";
            }
            if (AppTaskRules.CanAct(Row) === false) { return "<span class=\"tick tnone\" aria-hidden=\"true\"></span>"; }
            const State: string = AppTaskRules.StateOf(Row, Today);
            const Done: boolean = State === "done" || State === "dismissed";
            let Act: string = "done";
            let Label: string = "Mark done: ";
            if (Done === true) { Act = "reopen"; Label = "Reopen: "; }
            // ⚠ `aria-disabled`, NOT `disabled`, while the change is on its way: a disabled button cannot hold the
            // focus, so a tick pressed with Enter threw the keyboard to the top of the page (audit 2026-10-01). A second
            // press meanwhile is answered by `AppTaskActions` with a sentence - it changes nothing twice.
            let Off: string = "";
            if (Pending[Row.Id] === true) { Off = " aria-disabled=\"true\""; }
            return "<button type=\"button\" class=\"tick\" " + AppTaskView.ActAttr + "=\"" + Act + "\" "
                + AppTaskView.IdAttr + "=\"" + kHTMLCore.EscapeAttr(Row.Id) + "\" aria-pressed=\"" + String(Done)
                + "\" aria-label=\"" + Label + Title + "\"" + Off + ">" + AppIcons.Svg(AppIcons.Check, 13) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Lead");
            return "";
        }
    };

    /** contact · due chip · owner hint · age of the evidence · ↗ message. */
    private static Meta = (Row: AppRow, Today: string, Compact: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Bits: string[] = [];
            if (AppTaskRules.IsReply(Row) === false || Compact === true) {
                // ⭐ The contact's WhatsApp picture before the name - when there is one, or the silhouette
                // for a contact with no name (a bare number; `AppAvatars`).
                Bits.push("<span class=\"who\">" + AppAvatarRules.Small(Row.Avatar, AppTaskRules.NameOf(Row))
                    + kHTMLCore.EscapeHtml(AppTaskRules.NameOf(Row)) + "</span>");
            }
            // ⚠ NO CHIP ON A DECIDED OR SNOOZED ROW - "Overdue 2 d" on a task already done is noise.
            let Chip: AppTaskChip = AppTaskRules.Chip(Row, Today);
            if (AppTaskRules.IsOpen(Row, Today) === false) { Chip = new AppTaskChip(); }
            // ⚠ PREFIXED (`d-today`): a bare `today` class was dressed by the strip's own rule (measured).
            if (Chip.Text.length > 0) { Bits.push("<span class=\"due d-" + Chip.Cls + "\">" + kHTMLCore.EscapeHtml(Chip.Text) + "</span>"); }
            Bits.push(AppTaskView.StateWord(Row, Today));
            const Hint: string = AppTaskRules.OwnerHint(Row);
            // ⭐ OWNER 2026-11-02: "Your move" is superfluous - a chip only when it is not the owner's own move.
            if (Hint.length > 0 && Row.Owner !== "me" && Compact === false) { Bits.push(AppTaskView.OwnChip(Row, Hint)); }
            const Age: string = AppTaskRules.AgeOf(Row, Today);
            if (Age.length > 0 && Compact === false) { Bits.push("<span class=\"age\">" + kHTMLCore.EscapeHtml(Age) + "</span>"); }
            // ⭐ OWNER 2026-11-02: no "↗ message" button - the whole row (`a.trow`) already opens the message.
            return Bits.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Meta");
            return "";
        }
    };

    /** The owner hint with its explanation as a tooltip (`AppTaskRules.OwnerTip`), escaped. */
    private static OwnChip = (Row: AppRow, Hint: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Tip: string = kHTMLCore.EscapeAttr(AppTaskRules.OwnerTip(Row));
            let Attrs: string = "";
            if (Tip.length > 0) { Attrs = " title=\"" + Tip + "\" aria-label=\"" + Tip + "\""; }
            return "<span class=\"own\"" + Attrs + ">" + kHTMLCore.EscapeHtml(Hint) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.OwnChip");
            return "";
        }
    };

    /** "Snoozed to Mon 5 Oct" / "Dismissed" - the state a decided row is in, or "". */
    private static StateWord = (Row: AppRow, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const State: string = AppTaskRules.StateOf(Row, Today);
            let Word: string = "";
            if (State === "snoozed" && AppTaskRules.IsDay(Row.Until) === true) { Word = "until " + AppTaskRules.DayLabel(Row.Until); }
            if (State === "dismissed") { Word = "dismissed"; }
            if (State === "done") { Word = "done"; }
            if (Word.length === 0) { return ""; }
            return "<span class=\"tstate\">" + kHTMLCore.EscapeHtml(Word) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.StateWord");
            return "";
        }
    };

    /**
     * The "…" menu: Snooze (Tomorrow / Next week / a picked day), Dismiss - or Reopen on a decided
     * row - and "Reply about this" (opens the chat with the AI asked; it never sends).
     * ⚠ A `<details>`: it opens and closes without script, by keyboard too.
     */
    private static Menu = (Row: AppRow, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Id: string = kHTMLCore.EscapeAttr(Row.Id);
            const State: string = AppTaskRules.StateOf(Row, Today);
            const Out: string[] = ["<details class=\"tmenu\"><summary aria-label=\"More actions\" title=\"More actions\">"
                + AppIcons.Svg(AppIcons.More, 18) + "</summary><div class=\"tpop\" role=\"menu\">"];
            if (State === "open") {
                Out.push(AppTaskView.MenuButton("snooze", Id, AppTaskRules.SnoozeDay("tomorrow", Today), "Snooze until tomorrow"));
                const Next: string = AppTaskRules.SnoozeDay("nextweek", Today);
                Out.push(AppTaskView.MenuButton("snooze", Id, Next, "Snooze to next week (" + AppTaskRules.DayLabel(Next) + ")"));
                Out.push("<span class=\"tpick\"><input type=\"date\" aria-label=\"Snooze until\" min=\""
                    + kHTMLCore.EscapeAttr(AppTaskRules.AddDays(Today, 1)) + "\">"
                    + AppTaskView.MenuButton("pickdate", Id, "", "Snooze") + "</span>");
                if (AppTaskRules.IsReply(Row) === true) { Out.push(AppTaskView.MenuButton("done", Id, "", "Mark done")); }
                Out.push(AppTaskView.MenuButton("dismiss", Id, "", "Dismiss"));
            } else {
                Out.push(AppTaskView.MenuButton("reopen", Id, "", "Reopen"));
            }
            // ⭐ A reconnect suggestion asks the AI for a hello instead (`AppTaskRules.ReplyAsk`) - still never sends.
            let Ask: string = "Reply about this";
            if (AppTaskRules.IsReconnect(Row) === true) { Ask = "Help me say hello"; }
            if (Row.Href.length > 0) { Out.push(AppTaskView.MenuButton("reply", Id, "", Ask)); }
            Out.push("</div></details>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Menu");
            return "";
        }
    };

    /**
     * ⭐ A DECIDED ROW CARRIES A VISIBLE "Reopen" (owner, 2026-10-22: "Closing a task by mistake - ... each with
     * the option to re-open"): the same `reopen` action as the tick and the menu, one press away. "" on an open row.
     */
    public static ReopenButton = (Row: AppRow, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const State: string = AppTaskRules.StateOf(Row, Today);
            if (State !== "done" && State !== "dismissed") { return ""; }
            const Title: string = kHTMLCore.EscapeAttr(AppTaskRules.TitleOf(Row));
            return "<button type=\"button\" class=\"btn ghost sm treopen\" " + AppTaskView.ActAttr + "=\"reopen\" "
                + AppTaskView.IdAttr + "=\"" + kHTMLCore.EscapeAttr(Row.Id) + "\" aria-label=\"Reopen: " + Title + "\">"
                + AppTaskTips.ReopenWord + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.ReopenButton");
            return "";
        }
    };

    private static MenuButton = (Act: string, Id: string, Until: string, Words: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Day: string = "";
            if (Until.length > 0) { Day = " " + AppTaskView.UntilAttr + "=\"" + kHTMLCore.EscapeAttr(Until) + "\""; }
            return "<button type=\"button\" role=\"menuitem\" " + AppTaskView.ActAttr + "=\"" + Act + "\" "
                + AppTaskView.IdAttr + "=\"" + Id + "\"" + Day + ">" + kHTMLCore.EscapeHtml(Words) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.MenuButton");
            return "";
        }
    };

    // ── Adding one ───────────────────────────────────────────────────────────────────────────

    /**
     * ⭐ THE NEW-TASK ROW (owner, 2026-10-14: "ACT WHERE THE THING IS - NO SIDE FORM"): a row at the top of the
     * list the tasks are in, always there - type the task, pick who it is with, press Add (or Enter). It was a
     * "+ Add task" disclosure that floated a separate form over the list. Labels are for screen readers; the
     * placeholders and tooltips say what each box is for.
     */
    /**
     * ⭐ (owner review 2026-11-01) NO PLACEHOLDERS: what each box is for is its tooltip (`title`) and its screen-reader label.
     * `FixedHref` = the open chat's contact (the phone's "Add task" in a chat): no picker, a hidden field carries the href.
     * `FormId` names the form (the fields are `<FormId>-l/-c/-v/-d/-o`, read by `AppTaskBoard.Field` / `AppPhoneTaskAdd`).
     */
    public static AddForm = (Index: AppIndex, FixedHref: string = "", FormId: string = AppTaskView.AddFormId): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const F: string = FormId;
            let Who: string = "<label class=\"sr\" for=\"" + F + "-c\">With</label>" + AppTaskView.ContactPicker(Index, F + "-c");
            if (FixedHref.length > 0) {
                Who = "<input type=\"hidden\" id=\"" + F + "-c\" value=\"" + kHTMLCore.EscapeAttr(FixedHref) + "\">";
            }
            return "<form id=\"" + F + "\" class=\"tnew\" aria-label=\"Add a task\">"
                + "<span class=\"tick tnewico\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Plus, 14) + "</span>"
                + "<label class=\"sr\" for=\"" + F + "-l\">Task</label><input id=\"" + F + "-l\" class=\"tnewl\" maxlength=\""
                + String(AppTaskApi.MaxLabel) + "\" required autocomplete=\"off\""
                + " title=\"Add a task: what needs doing, e.g. Send the invoice\">"
                + Who
                + "<label class=\"sr\" for=\"" + F + "-v\">Detail</label><input id=\"" + F + "-v\" maxlength=\""
                + String(AppTaskApi.MaxValue) + "\" autocomplete=\"off\" title=\"A detail (optional)\">"
                + "<label class=\"sr\" for=\"" + F + "-d\">Due</label><input id=\"" + F + "-d\" type=\"date\" title=\"Due (optional)\">"
                + "<label class=\"sr\" for=\"" + F + "-o\">Whose move</label>"
                + "<select id=\"" + F + "-o\" title=\"Whose move\"><option value=\"me\">Mine</option><option value=\"them\">Theirs</option>"
                + "</select><button type=\"submit\" class=\"btn primary sm\">Add</button></form>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.AddForm");
            return "";
        }
    };

    /** The index's 1:1 contacts by name. ⚠ The option VALUE is the href the service matches. */
    private static ContactPicker = (Index: AppIndex, Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // No "Choose a contact" placeholder (owner review 2026-11-01): the tooltip says it; the empty first option stays (required).
            const Out: string[] = ["<select id=\"" + Id + "\" required title=\"Who the task is with\"><option value=\"\"></option>"];
            for (const One of AppTaskView.People(Index)) {
                Out.push("<option value=\"" + kHTMLCore.EscapeAttr(One.Href) + "\">" + kHTMLCore.EscapeHtml(One.Title) + "</option>");
            }
            Out.push("</select>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.ContactPicker");
            return "";
        }
    };

    /** 1:1 contacts with a page, sorted by name. */
    public static People = (Index: AppIndex): AppContact[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppContact[] = [];
            for (const One of Index.Contacts) {
                if (One.IsGroup === false && One.Href.length > 0) { Out.push(One); }
            }
            return Out.sort(AppTaskView.ByTitle);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.People");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: comparator. ERROR-SUPPRESSED-JUSTIFIED: pure - `People` is guarded. */
    private static ByTitle = (A: AppContact, B: AppContact): number => {
        const Al: string = A.Title.toLowerCase();
        const Bl: string = B.Title.toLowerCase();
        if (Al < Bl) { return -1; }
        if (Al > Bl) { return 1; }
        return 0;
    };

    // ── Empty ────────────────────────────────────────────────────────────────────────────────

    /** ⚠ EXPLAINS: where tasks come from, and whether the picture is complete yet. */
    public static Empty = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "<div class=\"tempty\"><p><b>No tasks yet.</b> A task appears when WACommander reads a "
                + "conversation and finds something promised - by you or by them - and when a message is waiting for "
                + "your reply. You can add one yourself with <b>Add task</b>.</p>";
            const Unread: number = Index.Unread();
            if (Unread > 0) {
                Out = Out + "<p>" + Unread.toLocaleString() + " of " + Index.Contacts.length.toLocaleString()
                    + " conversations have not been analysed yet, so this is not a complete picture.</p>";
            }
            return Out + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Empty");
            return "";
        }
    };

    private static NoneMatch = (Who: string, Search: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Search).trim().length > 0) {
                return "<p class=\"none\">No tasks match \"" + kHTMLCore.EscapeHtml(Search.trim()) + "\".</p>";
            }
            if (Who === "theirs") { return "<p class=\"none\">Nobody owes you anything right now.</p>"; }
            return "<p class=\"none\">Nothing on your list. Try <b>All</b> to see what others owe you.</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.NoneMatch");
            return "";
        }
    };

    // ── The Today strip on the chats screen ──────────────────────────────────────────────────

    /**
     * What is due now: at most five rows, a count, and "See all →". ⚠ "" (no strip at all) when
     * nothing is due - unless `Keep` (it was on screen and the last item was just ticked), when it
     * says "All done for today." instead of vanishing under the owner's finger.
     */
    public static Strip = (Index: AppIndex, Today: string, OffsetMin: number, Pending: Record<string, boolean>,
                           Keep: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Due: AppTaskStrip = AppTaskRules.Strip(Index, Today, OffsetMin);
            if (Due.Count === 0 && Keep === false) { return ""; }
            return "<section class=\"tasktoday\" id=\"" + AppTaskView.StripId + "\" aria-label=\"Due today\">"
                + "<div id=\"" + AppTaskView.StripListId + "\">" + AppTaskView.StripList(Due, Today, OffsetMin, Pending) + "</div>"
                + "<p class=\"tsay\" id=\"" + AppTaskView.StripSayId + "\" role=\"status\" aria-live=\"polite\"></p></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.Strip");
            return "";
        }
    };

    public static StripList = (Due: AppTaskStrip, Today: string, OffsetMin: number, Pending: Record<string, boolean>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ The header is a link to the Tasks tab too, like "See all →" (the box is one target, row by row).
            const Out: string[] = ["<header><h2><a class=\"tthead\" href=\"#tasks\" title=\"See all tasks\">Today</a></h2><i>" + Due.Count.toLocaleString()
                + "</i><a class=\"seeall\" href=\"#tasks\">See all" + AppIcons.Svg(AppIcons.Forward, 14) + "</a></header>"];
            if (Due.Count === 0) { Out.push("<p class=\"none\">All done for today.</p>"); return Out.join(""); }
            Out.push("<ul class=\"tasks\">");
            for (let At: number = 0; At < Due.Rows.length; At = At + 1) {
                Out.push(AppTaskView.Row(Due.Rows[At], At, Today, OffsetMin, Pending, true));
            }
            Out.push("</ul>");
            if (Due.Count > Due.Rows.length) {
                Out.push("<p class=\"more\"><a href=\"#tasks\">" + (Due.Count - Due.Rows.length).toLocaleString()
                    + " more due" + AppIcons.Svg(AppIcons.Forward, 14) + "</a></p>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskView.StripList");
            return "";
        }
    };
}
